<template>
  <component :is="bodyOnly ? 'div' : 'b-card'" no-body :class="{ 'd-flex flex-column h-100': !bodyOnly }">
    <b-card-header v-if="!bodyOnly" class="d-flex align-items-center pb-75" style="gap:8px">
      <h4 class="mb-0">Property Requests</h4>
      <b-badge v-if="total > 0" variant="light-danger" pill>{{ total }}</b-badge>
    </b-card-header>

    <div v-if="loading" class="text-center py-3">
      <b-spinner small variant="primary" />
      <div class="text-muted small mt-50">Loading…</div>
    </div>

    <b-table
      v-else
      :items="items"
      :fields="fields"
      responsive
      show-empty
      empty-text="No pending property requests"
      class="mb-0"
    >
      <template #cell(image)="{ item }">
        <b-img
          :src="item.coverImage || 'https://findbangkokroom.com/public/img/storage/1698858264911-picture.png'"
          rounded
          width="60"
          height="45"
          style="object-fit:cover"
        />
      </template>

      <template #cell(property)="{ item }">
        <router-link :to="{ name: 'property-edit', params: { id: item.id } }" class="font-weight-bold">
          {{ item.property_code }}
        </router-link>
        <div class="small text-muted">{{ formatDate(item.availble_date) }}</div>
      </template>

      <template #cell(owner)="{ item }">
        {{ item.owner_firstname }} {{ item.owner_lastname }}
      </template>

      <template #cell(actions)="{ item }">
        <b-button size="sm" variant="flat-success" class="btn-icon" @click="approve(item.id)">
          <feather-icon icon="CheckCircleIcon" size="16" />
        </b-button>
      </template>
    </b-table>

    <div class="d-flex justify-content-end px-1 pb-1" :class="{ 'pt-1': bodyOnly }">
      <b-button
        variant="primary"
        size="sm"
        :to="{ name: 'property-request-lists' }"
      >
        View All
      </b-button>
    </div>
  </component>
</template>

<script>
import {
  BCard, BCardHeader, BTable, BImg, BButton, BBadge, BSpinner,
} from 'bootstrap-vue'
import store from '@/store'
import axios from '@axios'
import {
  ref, watch, onUnmounted,
} from '@vue/composition-api'
import propertyStoreModule from '@/views/properties/propertiesStoreModule'
import { formatDate } from '@core/utils/filter'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

const STORE_KEY = 'app-property-dashboard'

export default {
  components: { BCard, BCardHeader, BTable, BImg, BButton, BBadge, BSpinner },

  props: {
    bodyOnly: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update:total'],

  setup(props, { emit }) {
    if (!store.hasModule(STORE_KEY)) store.registerModule(STORE_KEY, propertyStoreModule)
    onUnmounted(() => store.hasModule(STORE_KEY) && store.unregisterModule(STORE_KEY))

    const toast = useToast()
    const loading = ref(true)
    const items = ref([])
    const total = ref(0)
    watch(total, val => emit('update:total', val), { immediate: true })

    const fields = [
      { key: 'image', label: '', thStyle: { width: '70px' } },
      { key: 'property', label: 'Property' },
      { key: 'owner', label: 'Owner' },
      {
        key: 'actions', label: '', thStyle: { width: '60px' }, tdClass: 'text-center',
      },
    ]

    const fetchCoverImage = async (id) => {
      try {
        const { data } = await axios.get(`property/v1/coverimage/${id}`)
        const cover = (data.data || []).find(v => v.is_cover == 1)
        return cover ? cover.src : ''
      } catch {
        return ''
      }
    }

    const load = async () => {
      loading.value = true
      try {
        const res = await store.dispatch(`${STORE_KEY}/fetchProperties`, {
          perpage: 3,
          offset: 0,
          is_approve: 0,
          order_by: 'ID DESC',
        })
        const list = res?.data?.data || []
        total.value = res?.data?.total || list.length

        await Promise.all(list.map(async p => {
          p.coverImage = await fetchCoverImage(p.id)
        }))

        items.value = list
      } catch (e) {
        console.error(e)
      } finally {
        loading.value = false
      }
    }

    const approve = async id => {
      try {
        await store.dispatch(`${STORE_KEY}/approvePropertyLists`, [id])
        toast({ component: ToastificationContent, props: { title: 'Approved', icon: 'CheckCircleIcon', variant: 'success' } })
      } catch {
        toast({ component: ToastificationContent, props: { title: 'Error approving', icon: 'AlertTriangleIcon', variant: 'danger' } })
      } finally {
        load()
      }
    }

    load()

    return { loading, items, total, fields, approve, formatDate }
  },
}
</script>
